import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import IndexedDBService from '../services/IndexedDBService';
import ApiService from '../services/ApiService';

const AdminPanel = () => {
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  // Pipeline state
  const [pipelineMode, setPipelineMode] = useState('full'); // full|test
  const [pipelineStatus, setPipelineStatus] = useState(null);
  const [pipelinePolling, setPipelinePolling] = useState(false);

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    
    if (!selectedFile) {
      setFile(null);
      return;
    }
    
    // Check if file is CSV
    if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      fileInputRef.current.value = '';
      setFile(null);
      return;
    }
    
    setFile(selectedFile);
  };

  // Poll pipeline status while running
  useEffect(() => {
    let timer;

    const tick = async () => {
      try {
        const status = await ApiService.getPipelineStatus();
        setPipelineStatus(status);
        if (status.status === 'running') {
          setPipelinePolling(true);
        } else {
          setPipelinePolling(false);
        }
      } catch (e) {
        // ignore transient errors
      }
    };

    // Always get an initial status when admin loads
    tick();

    timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleRunPipeline = async () => {
    try {
      await ApiService.runPipeline(pipelineMode);
      toast.info(`Pipeline started (${pipelineMode})`);
    } catch (e) {
      toast.error(e.message || 'Failed to start pipeline');
    }
  };

  // Handle file upload
  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file to upload');
      return;
    }
    
    try {
      setUploading(true);
      
      // Simulate progress (since Airtable doesn't provide upload progress)
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          const newProgress = prev + 10;
          if (newProgress >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return newProgress;
        });
      }, 500);
      
      // Load file into local IndexedDB
      const result = await IndexedDBService.loadFromFileObject(file);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      toast.success(`Upload successful! ${result.recordCount} records processed.`);
      
      // Reset form after successful upload
      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
        setFile(null);
        fileInputRef.current.value = '';
      }, 1500);
      
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload file: ' + (error.message || 'Unknown error'));
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Cancel upload
  const handleCancel = () => {
    setFile(null);
    setUploadProgress(0);
    fileInputRef.current.value = '';
  };

  return (
    <div className="py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Admin Panel</h1>
        
        {/* Pipeline Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Daily Pipeline</h2>
          <p className="text-gray-600 mb-4">
            Run the backend analytics pipeline and import the merged <code>all_full.csv</code> into the UI database.
          </p>

          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-4">
              <label className="text-sm text-gray-700 font-medium">Mode</label>
              <select
                className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                value={pipelineMode}
                onChange={(e) => setPipelineMode(e.target.value)}
                disabled={pipelineStatus?.status === 'running'}
              >
                <option value="full">Full (all_p1..all_p6)</option>
                <option value="test">Test (all_test.csv)</option>
              </select>
            </div>

            <button
              onClick={handleRunPipeline}
              disabled={pipelineStatus?.status === 'running'}
              className={`px-4 py-2 rounded-md text-white ${pipelineStatus?.status === 'running' ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
            >
              {pipelineStatus?.status === 'running' ? 'Pipeline Running…' : 'Run Pipeline Now'}
            </button>
          </div>

          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-green-600 h-2.5 rounded-full transition-all"
                style={{ width: `${pipelineStatus?.percent ?? 0}%` }}
              ></div>
            </div>
            <div className="flex items-center justify-between mt-2">
              <p className="text-sm text-gray-700">
                <span className="font-medium">Status:</span> {pipelineStatus?.status || 'unknown'}
                {pipelineStatus?.message ? ` — ${pipelineStatus.message}` : ''}
              </p>
              <p className="text-sm text-gray-600">{pipelineStatus?.percent ?? 0}%</p>
            </div>
          </div>



          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div className="bg-gray-50 border border-gray-200 rounded p-3">
              <div className="text-gray-900 font-medium mb-1">Last successful run</div>
              <div className="text-gray-700">
                {pipelineStatus?.lastSuccessAt ? new Date(pipelineStatus.lastSuccessAt).toLocaleString() : '—'}
                {pipelineStatus?.lastSuccessMode ? ` (${pipelineStatus.lastSuccessMode})` : ''}
              </div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded p-3">
              <div className="text-gray-900 font-medium mb-1">Last finished run</div>
              <div className="text-gray-700">
                {pipelineStatus?.lastRunAt ? new Date(pipelineStatus.lastRunAt).toLocaleString() : '—'}
              </div>
            </div>
          </div>

          <details className="mt-4">
            <summary className="cursor-pointer text-sm text-blue-700">View run history</summary>
            <div className="mt-2 max-h-56 overflow-auto bg-gray-50 border border-gray-200 rounded p-3 text-xs font-mono whitespace-pre-wrap">
              {((pipelineStatus?.history || [])
                .slice()
                .reverse()
                .slice(0, 20))
                .map((r) => (
                  <div key={r.id}>
                    {r.startedAt} → {r.finishedAt || '…'} | {r.mode} | {r.status} | {r.message || ''}
                  </div>
                ))}
            </div>
          </details>

          <details className="mt-4">
            <summary className="cursor-pointer text-sm text-blue-700">View logs</summary>
            <div className="mt-2 max-h-56 overflow-auto bg-gray-50 border border-gray-200 rounded p-3 text-xs font-mono whitespace-pre-wrap">
              {(pipelineStatus?.log || []).slice(-120).map((l, idx) => (
                <div key={idx}>{l.ts} {l.line}</div>
              ))}
            </div>
          </details>
        </div>

        {/* CSV Upload Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Upload Stock Data</h2>
          <p className="text-gray-600 mb-6">
            Upload a CSV file to replace the current stock data. The CSV should match the format of the template.
            This will replace all existing data in the database.
          </p>
          
          <div className="space-y-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                ref={fileInputRef}
                className="hidden"
                id="csv-upload"
                disabled={uploading}
              />
              
              {!file ? (
                <div>
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  <p className="mt-2 text-gray-600">
                    Drag and drop your CSV file here, or{' '}
                    <label
                      htmlFor="csv-upload"
                      className="text-blue-600 hover:text-blue-800 cursor-pointer"
                    >
                      browse
                    </label>
                  </p>
                  <p className="text-xs text-gray-500 mt-1">CSV files only</p>
                </div>
              ) : (
                <div>
                  <svg
                    className="mx-auto h-12 w-12 text-green-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p className="mt-2 text-gray-800 font-medium">{file.name}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {(file.size / 1024).toFixed(2)} KB
                  </p>
                </div>
              )}
            </div>
            
            {file && !uploading && (
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Upload
                </button>
              </div>
            )}
            
            {uploading && (
              <div className="space-y-2">
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-blue-600 h-2.5 rounded-full"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-600 text-right">
                  {uploadProgress}% complete
                </p>
              </div>
            )}
          </div>
        </div>
        
        {/* Instructions Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">CSV Format Instructions</h2>
          
          <div className="prose max-w-none">
            <p>
              The CSV file should contain the following columns:
            </p>
            
            <ul className="list-disc pl-5 space-y-1 mb-4">
              <li>
                <strong>symbol</strong> - Stock ticker symbol
              </li>
              <li>
                <strong>signal_score</strong> - Numeric score (1-5)
              </li>
              <li>
                <strong>predicted_signal_score</strong> - Numeric score (1-5)
              </li>
              <li>
                <strong>predicted_direction</strong> - "up" or "down"
              </li>
              <li>
                <strong>trend_state</strong> - Numeric value
              </li>
              <li>
                <strong>trend_comment</strong> - Text description
              </li>
              <li>
                <strong>short_fname</strong> - Image filename (e.g., "AAPL.jpg")
              </li>
              <li>
                <strong>long_fname</strong> - Full image path
              </li>
              <li>
                <strong>companyName</strong> - Full company name
              </li>
              <li>
                <strong>industry</strong> - Industry category
              </li>
              <li>
                <strong>sector</strong> - Market sector
              </li>
              <li>
                <em>... and other financial metrics</em>
              </li>
            </ul>
            
            <p>
              <strong>Important Notes:</strong>
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                The first row should contain column headers
              </li>
              <li>
                All existing data will be replaced when uploading a new CSV
              </li>
              <li>
                Make sure image URLs are accessible
              </li>
              <li>
                Large files may take some time to process
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
